"use client";

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { MessageResponse } from "./message";
import { Badge } from "@/components/ui/badge";
import {
  extractFileToken,
  isDocxFile,
  isFileDownloadLink,
  isHtmlFile,
  isXlsxFile,
  isImageFile,
  isMarkdownFile,
  isPdfFile,
  isSourceFile,
  isSourceFileName,
} from "@/lib/file-links";
import { extractFileInfoFromToken } from "@/lib/file-link-rewrite";
import i18n from "@/lib/i18n";
import { projectUnavailableMarkdownLinks } from "@/lib/markdown-cross-file-links";
import { quoteHashMatches, renderedQuoteRange, resolveQuoteRange, type QuoteAnchor } from "@/lib/thread-quote-anchor";
import {
  conversationHeadingDomId,
  extractConversationHeadings,
  type ConversationHeading,
} from "@/lib/conversation-headings";

export interface InlineThreadQuote {
  childSessionId: string;
  anchor: QuoteAnchor;
  title?: string | null;
  onOpen: () => void;
}

/* ------------------------------------------------------------------ */
/*  Types                                                               */
/* ------------------------------------------------------------------ */

interface TextPart {
  type: "text";
  content: string;
  start: number;
}

interface ToolCallPart {
  type: "tool_call";
  status: "start" | "end" | "error";
  name: string;
}

type MessagePart = TextPart | ToolCallPart;

/* ------------------------------------------------------------------ */
/*  Parser                                                              */
/* ------------------------------------------------------------------ */

const TOOL_MARKER_RE = /(<<TOOL_CALL\|(start|end|error)\|([\s\S]*?)>>)/g;

function markerToolName(raw: string): string {
  if (!raw.startsWith("{")) return raw.trim() || "unknown";
  try {
    const envelope = JSON.parse(raw) as unknown;
    if (
      envelope &&
      typeof envelope === "object" &&
      !Array.isArray(envelope) &&
      typeof (envelope as Record<string, unknown>).toolName === "string"
    ) {
      return ((envelope as Record<string, string>).toolName || "unknown").slice(0, 256);
    }
  } catch {
    // Compatibility renderer intentionally has no detail source. Consume a
    // malformed structured marker as a status-only unknown badge rather than
    // exposing its raw payload in assistant text.
  }
  return "unknown";
}

function parseContent(text: string): MessagePart[] {
  const parts: MessagePart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Tracks which tool names currently have an unresolved "start" badge.
  // Duplicate starts are ignored; end/error update the existing badge in place.
  const activeTools = new Set<string>();

  while ((match = TOOL_MARKER_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: "text",
        content: text.slice(lastIndex, match.index),
        start: lastIndex,
      });
    }

    const status = match[2] as "start" | "end" | "error";
    const name = markerToolName(match[3]);

    if (status === "start") {
      if (!activeTools.has(name)) {
        parts.push({
          type: "tool_call",
          status: "start",
          name,
        });
        activeTools.add(name);
      }
      // else: duplicate start for an already-active tool — ignore.
    } else {
      // end or error
      if (activeTools.has(name)) {
        // Walk backwards to find the most recent active badge for this tool.
        for (let i = parts.length - 1; i >= 0; i--) {
          const part = parts[i];
          if (
            part.type === "tool_call" &&
            part.name === name &&
            part.status === "start"
          ) {
            part.status = status;
            activeTools.delete(name);
            break;
          }
        }
      }
      // else: duplicate end/error or end without start — ignore.
    }

    lastIndex = TOOL_MARKER_RE.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex), start: lastIndex });
  }

  return parts;
}

/* ------------------------------------------------------------------ */
/*  Plain-text rendering for user messages                             */
/* ------------------------------------------------------------------ */

/**
 * Match `http(s)://` URLs preceded by a word boundary. We avoid matching
 * schemes the user wouldn't have typed, and the character class stops at
 * whitespace / angle brackets / closing brackets / quotes so a URL ending
 * mid-sentence is captured cleanly.
 *
 * Examples:
 *   "see https://example.com"      → "https://example.com"
 *   "https://example.com."         → "https://example.com" + "."
 *   "https://a.test/path?q=1)"     → "https://a.test/path?q=1" + ")"
 *
 * The regex itself excludes `)`, `]`, `}` and quotes from the captured
 * body, so the only realistic trailing punctuation we need to pull off the
 * end of `match[0]` is the in-sentence ending punctuation that is still
 * valid inside a URL: `.`, `,`, `;`, `:`, `!`, `?`.
 */
const URL_REGEX = /\bhttps?:\/\/[^\s<>()"'\[\]{}]+/g;

// Tail-set that is still legal inside the URL character class but
// functionally closes the sentence the URL sits in. Trimming these off
// the end before generating the href keeps the surrounding text outside
// of the link.
const TRAILING_END_CHARS = new Set([".", ",", ";", ":", "!", "?"]);

interface LinkSegment {
  type: "text" | "link";
  content: string;
}

function splitOnUrls(text: string): LinkSegment[] {
  const segments: LinkSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Re-create the regex per scan so we control the lastIndex and avoid any
  // /g state leakage between renders when `text` changes.
  const re = new RegExp(URL_REGEX.source, "g");
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    // Pull sentence-ending punctuation off the end of the captured URL.
    // The trimmed chars become plain text segments so they render after
    // the link.
    let url = match[0];
    let trailing = "";
    while (url.length > 0 && TRAILING_END_CHARS.has(url[url.length - 1])) {
      trailing = url[url.length - 1] + trailing;
      url = url.slice(0, -1);
    }

    if (url) {
      segments.push({ type: "link", content: url });
    }
    if (trailing) {
      segments.push({ type: "text", content: trailing });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }
  return segments;
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

interface ParsedMessageContentProps {
  text: string;
  inlineThreadQuotes?: InlineThreadQuote[];
  /**
   * When `from === "user"`, render the message as plain text with newlines
   * preserved (`white-space: pre-line`) and URLs auto-linked. This matches
   * what chat users expect: typed line breaks go through, typed URLs are
   * clickable, but fenced code blocks / markdown headings don't suddenly
   * render.
   *
   * Defaults to assistant-style markdown rendering so existing call sites
   * (rendering of assistant text) keep working.
   */
  from?: "user" | "assistant";
  /**
   * Called when the user clicks an inline ``/files/d/{jwt}`` Markdown link
   * that points to a Markdown, HTML, PDF, or supported source file. The
   * parent opens the matching viewer. Unsupported file-download links and
   * external links open in a new tab normally.
   *
   * Only relevant for assistant messages (``from !== "user"``) — user
   * messages render as plain text with auto-linked URLs, no Markdown links.
   */
  onFileLinkClick?: (url: string, fileName: string) => void;
  messageId?: string;
  renderSurfaceId?: string;
  isStreaming?: boolean;
}

interface RenderedCharacter { node: Text; offset: number }

function renderedTextNodes(root: Node): { text: string; positions: Array<RenderedCharacter | null> } {
  const positions: Array<RenderedCharacter | null> = [];
  let text = "";
  let previousBlock: Element | null = null;
  const blockTags = new Set(["P", "DIV", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE", "TD", "TH"]);
  const collect = (parent: Node) => {
    for (const child of parent.childNodes) {
      if (child.nodeType !== Node.TEXT_NODE) { collect(child); continue; }
      const node = child as Text;
      if (!node.data) continue;
      let block: Element | null = node.parentElement;
      while (block && block !== root && !blockTags.has(block.tagName)) block = block.parentElement;
      if (block === root) block = null;
      if (text && block !== previousBlock && (block || previousBlock)) {
        text += "\n";
        positions.push(null);
      }
      for (let offset = 0; offset < node.data.length; offset++) {
        text += node.data[offset];
        positions.push({ node, offset });
      }
      previousBlock = block;
    }
  };
  collect(root);
  return { text, positions };
}

function wrapQuoteRanges(
  rendered: ReturnType<typeof renderedTextNodes>,
  highlights: Array<{ start: number; end: number; quote: InlineThreadQuote }>,
  allowNode: (node: Text) => boolean = () => true,
) {
  const segments = new Map<Text, Array<{ start: number; end: number; quote: InlineThreadQuote }>>();
  for (const { start, end, quote } of highlights) {
    for (let index = start; index < end; index++) {
      const position = rendered.positions[index];
      if (!position || !allowNode(position.node)) continue;
      const parts = segments.get(position.node) ?? [];
      const last = parts[parts.length - 1];
      if (last?.quote === quote && last.end === position.offset) last.end++;
      else parts.push({ start: position.offset, end: position.offset + 1, quote });
      segments.set(position.node, parts);
    }
  }
  for (const [node, parts] of segments) {
    for (const { start, end, quote } of parts.sort((a, b) => b.start - a.start)) {
      const selected = start ? node.splitText(start) : node;
      selected.splitText(end - start);
      const marker = document.createElement("span");
      marker.dataset.threadQuoteHighlight = quote.childSessionId;
      marker.setAttribute("role", "button");
      marker.setAttribute("tabindex", "0");
      marker.setAttribute("aria-label", i18n.t("chat:threads.openQuotedThread"));
      marker.className = "cursor-pointer bg-amber-500/50 text-inherit outline-none hover:bg-amber-500/65 focus-visible:ring-2 focus-visible:ring-primary";
      selected.replaceWith(marker);
      marker.appendChild(selected);
    }
  }
}

function QuotedMarkdown({ text, quotes }: { text: string; quotes: InlineThreadQuote[] }) {
  const rootRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let active = true;
    let verifiedQuotes: InlineThreadQuote[] | null = null;
    // Streamdown owns Markdown nodes. This keyed subtree is remounted whenever
    // its text or anchors change, so inserted spans never enter reconciliation.
    // It may render after our layout effect, so also observe its first commit.
    const applyHighlights = () => {
      if (!verifiedQuotes || root.querySelector('[data-thread-quote-highlight]')) return;
      const rendered = renderedTextNodes(root);
      const highlights: Array<{ start: number; end: number; quote: InlineThreadQuote }> = [];
      for (const quote of verifiedQuotes) {
        if (quote.anchor.source_quote_start == null && quote.anchor.source_quote_end == null) {
          // Tool markers split a message into Markdown parts. An offsetless
          // quote must be unique across all of them, not just this part.
          const messageText = Array.from(
            root.parentElement?.querySelectorAll('[data-thread-quote-text-part]') ?? [],
            (part) => renderedTextNodes(part).text,
          ).join("\uFFFC");
          if (!renderedQuoteRange("", messageText, quote.anchor)) continue;
        }
        const range = renderedQuoteRange(text, rendered.text, quote.anchor);
        if (!range) continue;
        if (highlights.some((other) => range.start < other.end && range.end > other.start)) continue;
        highlights.push({ ...range, quote });
      }
      wrapQuoteRanges(rendered, highlights);
    };
    const observer = new MutationObserver(applyHighlights);
    observer.observe(root.parentElement ?? root, { childList: true, characterData: true, subtree: true });
    void Promise.all(quotes.map((quote) => quoteHashMatches(quote.anchor))).then((valid) => {
      if (!active) return;
      verifiedQuotes = quotes.filter((_, index) => valid[index]);
      applyHighlights();
    });
    let retries = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const retry = () => {
      if (root.querySelector('[data-thread-quote-highlight]') || retries++ >= 20) return;
      applyHighlights();
      timer = setTimeout(retry, 25);
    };
    timer = setTimeout(retry, 25);
    return () => {
      active = false;
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [text, quotes]);

  const openFor = (target: EventTarget | null) => {
    const marker = target instanceof Element
      ? target.closest<HTMLElement>('[data-thread-quote-highlight]') : null;
    return quotes.find((quote) => quote.childSessionId === marker?.dataset.threadQuoteHighlight);
  };
  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const quote = openFor(event.target);
    if (!quote) return;
    event.preventDefault();
    event.stopPropagation();
    quote.onOpen();
  };
  const onKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const quote = openFor(event.target);
    if (!quote) return;
    event.preventDefault();
    event.stopPropagation();
    quote.onOpen();
  };
  return <div ref={rootRef} data-thread-quote-text-part onClickCapture={onClickCapture} onKeyDownCapture={onKeyDownCapture}>
    <MessageResponse>{text}</MessageResponse>
  </div>;
}

function CrossPartQuotes({ quotes, children, onClickCapture, messageId, renderSurfaceId, headings }: {
  quotes: InlineThreadQuote[];
  children: ReactNode;
  onClickCapture: (event: MouseEvent<HTMLDivElement>) => void;
  messageId?: string;
  renderSurfaceId?: string;
  headings: readonly ConversationHeading[];
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const [hoveredMarker, setHoveredMarker] = useState<HTMLElement | null>(null);
  const [focusedMarker, setFocusedMarker] = useState<HTMLElement | null>(null);
  const activeMarker = focusedMarker ?? hoveredMarker;
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const tooltipQuote = quotes.find((quote) => quote.childSessionId === activeMarker?.dataset.threadQuoteHighlight);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !messageId || !renderSurfaceId) return;
    const register = () => {
      root.querySelectorAll<HTMLElement>("h1,h2,h3,h4,h5,h6").forEach((element, index) => {
        const heading = headings[index];
        if (!heading) return;
        element.id = conversationHeadingDomId(renderSurfaceId, messageId, heading.key);
        element.dataset.headingKey = heading.key;
        element.dataset.headingOrdinal = String(heading.ordinal);
        element.dataset.headingFingerprint = heading.contentFingerprint;
        element.tabIndex = -1;
        element.classList.add("scroll-mt-16", "outline-none", "focus-visible:ring-2");
      });
    };
    register();
    const observer = new MutationObserver(register);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [headings, messageId, renderSurfaceId]);
  useLayoutEffect(() => {
    if (!activeMarker) return;
    activeMarker.setAttribute("aria-describedby", tooltipId);
    const position = () => {
      if (!activeMarker.isConnected) {
        setHoveredMarker(null);
        setFocusedMarker(null);
        return;
      }
      const rect = activeMarker.getBoundingClientRect();
      setTooltipPosition({
        left: Math.max(Math.min(144, window.innerWidth / 2), Math.min(rect.left + rect.width / 2, Math.max(window.innerWidth - 144, window.innerWidth / 2))),
        top: rect.bottom + 6,
      });
    };
    position();
    window.addEventListener("scroll", position, true);
    window.addEventListener("resize", position);
    return () => {
      activeMarker.removeAttribute("aria-describedby");
      window.removeEventListener("scroll", position, true);
      window.removeEventListener("resize", position);
    };
  }, [activeMarker, tooltipId]);
  const markerFor = (target: EventTarget | null) => {
    const marker = target instanceof Element
      ? target.closest<HTMLElement>('[data-thread-quote-highlight]') : null;
    return marker && rootRef.current?.contains(marker) ? marker : null;
  };
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !quotes.some((quote) => quote.anchor.source_quote_start == null
      && quote.anchor.source_quote_end == null)) return;
    let active = true;
    let applied = false;
    let verified: InlineThreadQuote[] | null = null;
    const apply = () => {
      if (applied || !verified) return;
      const rendered = renderedTextNodes(root);
      const highlights: Array<{ start: number; end: number; quote: InlineThreadQuote }> = [];
      for (const quote of verified) {
        const range = renderedQuoteRange("", rendered.text, quote.anchor);
        if (!range) continue;
        const parts = new Set<Element>();
        for (let index = range.start; index < range.end; index++) {
          const part = rendered.positions[index]?.node.parentElement?.closest('[data-thread-quote-text-part]');
          if (part) parts.add(part);
        }
        if (parts.size < 2) continue;
        if (highlights.some((other) => range.start < other.end && range.end > other.start)) continue;
        if (rendered.positions.slice(range.start, range.end).some((position) =>
          position?.node.parentElement?.closest('[data-thread-quote-highlight]'))) continue;
        highlights.push({ ...range, quote });
      }
      if (!highlights.length) return;
      applied = true;
      wrapQuoteRanges(rendered, highlights, (node) => Boolean(node.parentElement?.closest('[data-thread-quote-text-part]')));
    };
    const observer = new MutationObserver(apply);
    observer.observe(root, { childList: true, characterData: true, subtree: true });
    void Promise.all(quotes.map((quote) => quoteHashMatches(quote.anchor))).then((valid) => {
      if (!active) return;
      verified = quotes.filter((quote, index) => valid[index]
        && quote.anchor.source_quote_start == null && quote.anchor.source_quote_end == null);
      apply();
    });
    let retries = 0;
    const timer = setInterval(() => {
      if (applied || retries++ >= 20) { clearInterval(timer); return; }
      apply();
    }, 25);
    return () => { active = false; observer.disconnect(); clearInterval(timer); };
  }, [quotes]);
  return <>
    <div ref={rootRef} className="flex flex-col gap-1" data-quote-body="true" onClickCapture={onClickCapture}
      data-message-id={messageId}
      data-conversation-render-surface={renderSurfaceId}
      onMouseOverCapture={(event) => {
        const marker = markerFor(event.target);
        if (marker) setHoveredMarker(marker);
      }}
      onMouseOutCapture={(event) => {
        const marker = markerFor(event.target);
        if (marker && marker !== markerFor(event.relatedTarget)) setHoveredMarker(null);
      }}
      onFocusCapture={(event: FocusEvent<HTMLDivElement>) => setFocusedMarker(markerFor(event.target))}
      onBlurCapture={(event: FocusEvent<HTMLDivElement>) => {
        if (markerFor(event.target) !== markerFor(event.relatedTarget)) setFocusedMarker(null);
      }}>
      {children}
    </div>
    {activeMarker && tooltipQuote && tooltipPosition && createPortal(
      <div id={tooltipId} role="tooltip" className="pointer-events-none fixed z-50 max-w-72 -translate-x-1/2 rounded-md border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md"
        style={{ left: tooltipPosition.left, top: tooltipPosition.top }}>
        {tooltipQuote.title?.trim()
          ? i18n.t("chat:threads.quotedThreadTooltip", { title: tooltipQuote.title.trim() })
          : i18n.t("chat:threads.openThread")}
      </div>,
      document.body,
    )}
  </>;
}

/**
 * Render a user message.
 *
 * Pure-text rendering (no Markdown) with two niceties:
 *   - newlines preserved via `white-space: pre-line`,
 *   - URLs auto-linked.
 *
 * `break-words` lets long unbroken tokens wrap inside the bubble.
 */
function UserMessageBody({ text }: { text: string }) {
  const segments = useMemo(() => splitOnUrls(text), [text]);
  return (
    <div className="whitespace-pre-line break-words">
      {segments.map((seg, i) =>
        seg.type === "link" ? (
          <a
            key={i}
            className="text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
            href={seg.content}
            rel="noopener noreferrer"
            target="_blank"
          >
            {seg.content}
          </a>
        ) : (
          // Each text segment is its own element so the browser still
          // honours whitespace (and `pre-line`) inside the segment, but
          // newlines from line breaks in the segment flow across to the
          // next segment via CSS whitespace handling.
          <span key={i}>{seg.content}</span>
        )
      )}
    </div>
  );
}

/** Legacy/raw-marker fallback: intentionally status-only and non-fetching. */
function ParsedToolCallBadge({ status, name }: ToolCallPart) {
  const variant = status === "error" ? "destructive" : status === "start" ? "outline" : "secondary";
  const icon = status === "error" ? "❌" : status === "start" ? "▶️" : "✅";
  return <Badge variant={variant}>{icon} {name}</Badge>;
}

function sourceFallbackName(url: string, linkText: string): string {
  const token = extractFileToken(url);
  const fileName = token ? extractFileInfoFromToken(token)?.fileName : null;
  if (fileName && isSourceFileName(fileName)) return fileName;
  if (isSourceFileName(linkText)) return linkText;
  return "document.json";
}

export function ParsedMessageContent({ text, from, onFileLinkClick, inlineThreadQuotes = [], messageId, renderSurfaceId, isStreaming = false }: ParsedMessageContentProps) {
  useTranslation();
  // Tool-call markers only ever come from the agent runtime, never from
  // users. Skipping the parse step for user messages keeps the render path
  // simple and avoids the cost when there's no marker to match. The user
  // path is also responsible for honouring newlines — see UserMessageBody.
  if (from === "user") {
    return <UserMessageBody text={text} />;
  }

  const parts = useMemo(() => parseContent(text), [text]);
  const [headings, setHeadings] = useState<ConversationHeading[]>([]);
  const parsedHeadingRef = useRef<{ messageId: string; renderSurfaceId: string; text: string } | null>(null);
  useEffect(() => {
    let active = true;
    // A growing reply changes every frame. Parsing its entire Markdown here
    // would retain overlapping ASTs/promises until their hashes settle.
    if (!messageId || !renderSurfaceId || isStreaming) {
      if (parsedHeadingRef.current?.messageId !== messageId
        || parsedHeadingRef.current?.renderSurfaceId !== renderSurfaceId
        || parsedHeadingRef.current?.text !== text) {
        setHeadings((current) => current.length ? [] : current);
      }
      return () => { active = false; };
    }
    void extractConversationHeadings(text).then((next) => {
      if (active) {
        parsedHeadingRef.current = { messageId, renderSurfaceId, text };
        setHeadings(next);
      }
    });
    return () => { active = false; };
  }, [messageId, renderSurfaceId, text, isStreaming]);
  const headingRevision = headings.map((heading) => heading.key).join(":");

  // ── Click interception for inline file-download links ───────────────────
  // With MessageResponse's supported `linkSafety: { enabled: false }` setting,
  // Streamdown renders links as ``<a data-streamdown="link" href="…">``.
  // We listen via ``onClickCapture`` which fires top-down (parent → child)
  // *before* the anchor's default click behaviour. For Markdown, HTML, PDF,
  // DOCX, image, and CodeMirror-supported source file-download links we call
  // ``preventDefault`` and ``stopPropagation`` so the browser doesn't
  // navigate, then open the matching viewer instead.
  //
  // Other file-download links and external links are left untouched and
  // open in a new tab via the anchor's ``target="_blank"``.
  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onFileLinkClick) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-thread-quote-highlight]')) return;
    const linkEl = target.closest('[data-streamdown="link"]') as HTMLElement | null;
    if (!linkEl) return;
    // The href is the normal app path. Keep the data-href fallback only for a
    // caller that explicitly turns Streamdown's link-safety modal back on.
    const href = linkEl.getAttribute("href") ?? linkEl.getAttribute("data-href");
    if (!href) return;
    const linkText = linkEl.textContent ?? "";
    const isMd = isMarkdownFile(href, linkText);
    const isHtml = isHtmlFile(href, linkText);
    const isXlsx = isXlsxFile(href, linkText);
    const isPdf = isPdfFile(href, linkText);
    const isDocx = isDocxFile(href, linkText);
    const isImage = isImageFile(href, linkText);
    const isSource = isSourceFile(href, linkText);
    if (isFileDownloadLink(href) && (isMd || isHtml || isXlsx || isPdf || isDocx || isImage || isSource)) {
      e.preventDefault();
      e.stopPropagation();
      const fallbackName = isHtml
        ? "document.html"
        : isXlsx
          ? i18n.t("chat:singleFile.defaultWorkbookName")
        : isPdf
          ? "document.pdf"
          : isDocx
            ? "document.docx"
            : isImage
              ? "document.png"
              : "document.md";
      onFileLinkClick(
        href,
        isSource ? sourceFallbackName(href, linkText) : linkText || fallbackName,
      );
    }
  };

  return (
    <CrossPartQuotes key={inlineThreadQuotes.length
      ? `${text}:${JSON.stringify(inlineThreadQuotes.map((quote) => [quote.childSessionId, quote.anchor]))}`
      : messageId ?? "unquoted"}
      quotes={inlineThreadQuotes} onClickCapture={handleClickCapture}
      messageId={messageId} renderSurfaceId={renderSurfaceId} headings={headings}>
      {parts.map((part, i) =>
        part.type === "text" ? (
          (() => {
            const quotes = inlineThreadQuotes.flatMap((quote) => {
              const range = resolveQuoteRange(text, quote.anchor);
              if (!range && quote.anchor.source_quote_start == null
                && quote.anchor.source_quote_end == null) return [quote];
              if (!range || range.source_quote_start < part.start
                || range.source_quote_end > part.start + part.content.length) return [];
              return [{ ...quote, anchor: {
                ...quote.anchor,
                source_quote_start: range.source_quote_start - part.start,
                source_quote_end: range.source_quote_end - part.start,
              } }];
            });
            const safeContent = projectUnavailableMarkdownLinks(part.content, "sourceUnknown");
            return quotes.length
              ? <QuotedMarkdown key={`${i}:${part.content}:${JSON.stringify(quotes.map((quote) => [quote.childSessionId, quote.anchor]))}`} text={safeContent} quotes={quotes} />
              : <MessageResponse key={i} navigationIdentity={messageId} headingRevision={headingRevision}>{safeContent}</MessageResponse>;
          })()
        ) : (
          <ParsedToolCallBadge key={i} {...part} />
        )
      )}
    </CrossPartQuotes>
  );
}
